import React, { useState, useEffect } from 'react';
import { KeyboardArrowDown } from '@mui/icons-material';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import InputAdornment from '@mui/material/InputAdornment';
import List from '@mui/material/List';
import Tooltip from '@mui/material/Tooltip';
import { TextField } from '../../..';
import { Typography } from '../../Typography';
import type { BaseAutocompleteProps, BaseAutocompleteValue, MUIAutocompleteProps } from '../types';
import { OptionsListAddAction } from './components';
import { BaseAutocompleteChips } from './components/BaseAutocompleteChips';
import { INPUT_MIN_WIDTH } from './utils/chipLayoutConstants';
import { createRenderOption } from './utils/createRenderOption';

/**
 * BaseAutocomplete is a component that renders a base autocomplete component.
 * Simply a generic wrapper around the Autocomplete component, with our own default styles & props.
 * @returns BaseAutocomplete component
 */
export const BaseAutocomplete = React.forwardRef<HTMLDivElement, BaseAutocompleteProps>((props, ref) => {
  const {
    t,
    idPrefix,
    options,
    disabled,
    label,
    placeholder,
    value,
    multiple,
    startAdornment,
    optionsListAddAction,
    topOfListAdornment,
    // fallback to the option value if no mapOptionToValue is provided
    mapOptionToValue = (val) => val as ReturnType<BaseAutocompleteProps['mapOptionToValue']>,
    onScrollToBottom,
    readOnly,
    readOnlyText,
    error,
    helperText,
    onBlur,
    isListAvatarVisible,
    maxValues,
    inlineLabel = false,
    size = 'medium',
    showLabel = false,
    displayUidInsteadOfLabelOnInput,
    showHelpIcon,
    helpMessage,
    clearable = false,
    forcePopupIcon = false,
    closeComponent,
    ...muiAutocompleteProps
  } = props as BaseAutocompleteProps & Record<string, unknown>;
  const [open, setOpen] = useState<boolean>(false);

  useEffect(() => {
    if (closeComponent?.shouldClose) {
      setOpen(false);
      closeComponent?.reset();
    }
  }, [closeComponent]);

  const getPlaceholder = () => {
    if (multiple && value.length > 0) return '';
    if (!maxValues || !multiple) return placeholder;
    if (value.length >= maxValues) return '';
    return placeholder;
  };

  const inlineLabelStartAdornment =
    inlineLabel && label ? (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignContent: 'center',
          marginLeft: 0,
          marginRight: 0,
          backgroundColor: 'transparent',
        }}
      >
        <InputAdornment position="start" sx={{ m: 0 }}>
          <Typography variant="body1" sx={{ color: '#414651', ml: 1 }}>
            {label}:
          </Typography>
        </InputAdornment>
      </Box>
    ) : undefined;

  return (
    <Box ref={ref} sx={{ width: muiAutocompleteProps.fullWidth ? '100%' : 'auto' }}>
      {label && !inlineLabel && showLabel && (
        <Typography
          variant="body1"
          sx={{
            ml: '2px',
            mb: '4px',
            display: 'block',
            fontSize: '14px',
            fontWeight: '500',
            lineHeight: '20px',
            color: '#344054',
          }}
        >
          {label}
        </Typography>
      )}
      <Tooltip title={readOnly ? readOnlyText : undefined} placement="top-end">
        <Autocomplete
          {...(muiAutocompleteProps as MUIAutocompleteProps<boolean>)}
          readOnly={readOnly}
          multiple={multiple}
          disabled={disabled}
          options={options}
          value={value}
          size={size}
          open={open}
          onOpen={() => {
            if (maxValues && multiple && value.length >= maxValues) {
              setOpen(false);
              return;
            }
            setOpen(true);
          }}
          onClose={() => setOpen(false)}
          onBlur={(e) => {
            setOpen(false);
            onBlur?.(e);
          }}
          defaultValue={undefined}
          loadingText={t('ns.common:loading')}
          onChange={(_e, newValue) => {
            if (multiple) {
              // TypeScript correctly infers the callback type here because we did not destructure `onChange`.
              (props as Extract<BaseAutocompleteProps, { multiple: true }>).onChange(
                newValue as BaseAutocompleteValue[],
              );
            } else {
              (props as Extract<BaseAutocompleteProps, { multiple?: false }>).onChange(
                newValue as BaseAutocompleteValue | null,
              );
            }
          }}
          popupIcon={<KeyboardArrowDown sx={{ color: '#A4A7AE' }} />}
          forcePopupIcon={forcePopupIcon}
          isOptionEqualToValue={(opt: BaseAutocompleteValue, val: BaseAutocompleteValue) => {
            return opt?.uid === val?.uid;
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              showHelpIcon={showHelpIcon}
              helpMessage={helpMessage}
              placeholder={getPlaceholder()}
              id={`${idPrefix}-autocomplete-input`}
              error={error}
              inputStyles={{
                '& .MuiInputAdornment-root ': {
                  marginLeft: '0 !important',
                },
              }}
              helperText={helperText}
              slotProps={{
                input: {
                  ...params.InputProps,
                  // When multiple is true, the chips are rendered through the startAdornment
                  startAdornment: (
                    <>
                      {startAdornment && <Box sx={{ marginRight: '4px' }}>{startAdornment}</Box>}
                      {inlineLabelStartAdornment}
                      {params.InputProps.startAdornment}
                    </>
                  ),
                },
                htmlInput: {
                  ...params.inputProps,
                  id: `${idPrefix}-autocomplete-input`,
                  'aria-label': label,
                  onDoubleClick: (e: React.MouseEvent<HTMLInputElement>) => {
                    // Select all text on double-click for better UX
                    e.currentTarget.select();
                  },
                },
                inputLabel: params.InputLabelProps,
              }}
            />
          )}
          /**
           * By default the autocomplete renders all the hidden tags when the input is focused.
           * This is not what we want, so we need to override the default behavior.
           */
          renderValue={
            multiple
              ? (values, getTagProps) => (
                  <BaseAutocompleteChips
                    values={values as BaseAutocompleteValue[]}
                    getTagProps={getTagProps}
                    t={t}
                    disabled={disabled}
                    mapOptionToValue={mapOptionToValue}
                    onToggleOpen={() => setOpen((prev) => !prev)}
                  />
                )
              : undefined
          }
          noOptionsText={
            optionsListAddAction ? (
              <OptionsListAddAction {...optionsListAddAction} hasBottomBorder={false} />
            ) : (
              <Box sx={{ marginY: 3, marginX: 4 }}>{t('ns.common:type_to_search')}</Box>
            )
          }
          slots={{
            listbox: List,
          }}
          slotProps={{
            paper: {
              sx: {
                marginTop: '2px',
                borderRadius: '8px',
                outline: '1px solid #F2F4F7',
                minWidth: 'max-content',
                '& .MuiAutocomplete-noOptions': {
                  padding: 0,
                },
                '& .MuiAutocomplete-listbox ': {
                  padding: optionsListAddAction ? 0 : '4px 0',
                  maxHeight: '300px',
                  minWidth: 'max-content',
                },
              },
              onScroll: (e: React.SyntheticEvent) => {
                const listboxNode = e.currentTarget;
                if (onScrollToBottom && listboxNode.scrollTop + listboxNode.clientHeight === listboxNode.scrollHeight) {
                  onScrollToBottom();
                }
              },
            },
          }}
          getOptionKey={(option: BaseAutocompleteValue) => option.uid}
          getOptionLabel={(option: BaseAutocompleteValue) => {
            const currentOption = mapOptionToValue(option);
            return displayUidInsteadOfLabelOnInput ? currentOption.uid : currentOption.label;
          }}
          renderOption={createRenderOption({
            mapOptionToValue,
            optionsListAddAction,
            topOfListAdornment,
            isAvatarVisible: isListAvatarVisible,
          })}
          filterOptions={muiAutocompleteProps.filterOptions || ((x) => x)}
          sx={{
            ...muiAutocompleteProps.sx,
            borderRadius: '8px',
            backgroundColor: disabled ? '#F2F4F7' : undefined,
            // Hide or show the clear icon btn based on the clearable prop
            '& .MuiAutocomplete-clearIndicator': {
              display: clearable ? 'flex' : 'none',
            },
            '& .MuiFilledInput-root': {
              // We need to use !important here because the TextField component overrides the padding on the right
              padding: '0 14px 0 12px !important',
              flexWrap: 'nowrap',
              '& .MuiInputBase-input': {
                /**
                 * The autocomplete component adds inconsistent padding to the input, which we don't want.
                 * Usually visible when the input is disabled.
                 */
                height: '24px !important',
                padding: size === 'medium' ? '12px 4px !important' : '8px 20px 8px 4px!important',
                /**
                 * Input grows to fill remaining space after chips with a minimum width.
                 * flex-grow: 1 allows it to expand, flex-shrink: 0 prevents shrinking below minWidth.
                 */
                flex: '1 0 auto',
                minWidth: `${INPUT_MIN_WIDTH}px`,
              },
            },
            '& .MuiAutocomplete-tag': {
              marginTop: 0,
              marginBottom: 0,
            },
          }}
        />
      </Tooltip>
    </Box>
  );
});

BaseAutocomplete.displayName = 'BaseAutocomplete';
